/**
 * Draws a level's objects in a view: ground and ceilings with glowing edges, blocks (fill,
 * tile pattern, bright outline), slopes (runs of 'slope' stair columns as one smooth shape),
 * spikes (dark with a light outline so they read on every backdrop), pads, orbs, portals,
 * boost pickups and the finish line.
 *
 * What moves is drawn where the physics has it at the view's moment of the level
 * (LevelDrawState.tick, fractional: smooth between ticks) — the same functions of the tick
 * as the physics (game/motion.ts): spinning saw blades (a spin blur, sparks where they cut
 * into a surface) and rotors' spiked bars and balls on chains, with their tracks and rails,
 * under the level's blocks (a blade half sunk into the ground shows its top half); moving blocks
 * over everything else of the level, spikes included — a platform sliding over a spike pit is
 * solid to land on and must look it — but for the still ground they sink into.
 *
 * Only the objects in view are drawn (World.objectsInRange with a reused array), and shapes
 * of one kind are batched into one path per pass, so a frame costs a few dozen draw calls
 * whatever the level. Glows are pre-drawn sprites; no shadowBlur.
 */
import {
  type Aabb,
  BOOSTS,
  type LevelData,
  type LevelObject,
  type Motion,
  motionOffset,
  type Offset,
  type OrbColor,
  type PadColor,
  type PortalType,
  ROTOR,
  rotorTurn,
  type SpikeObject,
  sweptBounds,
  turnCos,
  turnSin,
  type World,
} from '@cube-rush/shared';
import { stamp } from './canvas';
import { mix, rgba } from './color';
import { drawEmojiSprite, glowSprite, SizedSprite } from './sprites';
import type { Theme } from './themes';
import type { ViewTransform } from './view';

export const PAD_COLORS: Readonly<Record<PadColor, string>> = {
  yellow: '#ffd60a',
  pink: '#ff4fd8',
  blue: '#2de2ff',
};

export const ORB_COLORS: Readonly<Record<OrbColor, string>> = {
  yellow: '#ffd60a',
  pink: '#ff4fd8',
  blue: '#2de2ff',
  green: '#3dff8b',
};

export const PORTAL_COLORS: Readonly<Record<PortalType, string>> = {
  'gravity-normal': '#2de2ff',
  'gravity-reverse': '#ffd60a',
  'mode-cube': '#3dff8b',
  'mode-ship': '#ff4fd8',
  'mode-ball': '#ff3b5c',
  'mode-ufo': '#ff9a1f',
  'mode-wave': '#8c6bff',
  'mode-robot': '#d9e2f2',
  'mode-spider': '#b65cff',
  'speed-0': '#ffb238',
  'speed-1': '#4fb4ff',
  'speed-2': '#3dff8b',
  'speed-3': '#ff4fd8',
};

/** Chevrons on a speed portal: one per speed step (a table: no string work per frame). */
const SPEED_CHEVRONS: Partial<Record<PortalType, number>> = {
  'speed-0': 1,
  'speed-1': 2,
  'speed-2': 3,
  'speed-3': 4,
};

/**
 * A run of touching 'slope' columns drawn as one shape (see BLOCK_STYLES): its outline in
 * world coordinates, from the surface it stands on (or hangs from) along its open side and
 * back. The open side follows the stairs at their own 45° wherever they step, through the
 * corners on the open side, so the shape covers every column (never less solid than the
 * physics has) and a flat stretch stays flat.
 */
export interface SlopeShape {
  x1: number;
  x2: number;
  /**
   * The open side as x, y pairs, from the surface the run stands on (or hangs from) at x1 to
   * it again at x2: closed along that surface it is the shape.
   */
  outline: number[];
}

/** The slope shapes of a level's objects (runs on the floor and runs hanging from a ceiling). */
export function slopeShapes(objects: readonly LevelObject[]): SlopeShape[] {
  const columns = objects.filter(
    (o): o is LevelObject & { type: 'block' } => o.type === 'block' && o.style === 'slope',
  );
  const shapes: SlopeShape[] = [];
  // Standing on the floor (their bottom at or below it), else hanging from a ceiling.
  for (const standing of [true, false]) {
    const side = columns.filter((o) => o.y <= 1e-9 === standing).sort((a, b) => a.x - b.x);
    let run: Array<LevelObject & { type: 'block' }> = [];
    const flush = () => {
      if (run.length > 0) shapes.push(slopeShape(run, standing));
      run = [];
    };
    for (const o of side) {
      const last = run[run.length - 1];
      const base = (b: { y: number; h: number }) => (standing ? b.y : b.y + b.h);
      if (last && (Math.abs(last.x + last.w - o.x) > 1e-6 || base(last) !== base(o))) flush();
      run.push(o);
    }
    flush();
  }
  return shapes;
}

/** Steps (blocks) at which a slope's open side is sampled: stair corners and crossings fall on it. */
const SLOPE_STEP = 0.25;

function slopeShape(run: ReadonlyArray<LevelObject>, standing: boolean): SlopeShape {
  // Distances from the surface the run stands on / hangs from, so both cases are one.
  const base = standing ? run[0]!.y : run[0]!.y + run[0]!.h;
  const depth = (o: LevelObject) => (standing ? o.y + o.h - base : base - o.y);
  const reach = (x: number) => {
    let d = 0;
    for (const o of run) d = Math.max(d, depth(o) - Math.max(0, o.x - x, x - (o.x + o.w)));
    return d;
  };
  let x1 = Number.POSITIVE_INFINITY;
  let x2 = Number.NEGATIVE_INFINITY;
  for (const o of run) {
    x1 = Math.min(x1, o.x - depth(o));
    x2 = Math.max(x2, o.x + o.w + depth(o));
  }
  const outline: number[] = [];
  const point = (x: number) => outline.push(x, standing ? base + reach(x) : base - reach(x));
  for (let x = x1; x < x2 - 1e-9; x += SLOPE_STEP) point(x);
  point(x2);
  // Drop the points in the middle of straight stretches.
  const kept: number[] = [outline[0]!, outline[1]!];
  for (let i = 2; i + 2 < outline.length; i += 2) {
    const [ax, ay] = [kept[kept.length - 2]!, kept[kept.length - 1]!];
    const [bx, by, cx, cy] = [outline[i]!, outline[i + 1]!, outline[i + 2]!, outline[i + 3]!];
    if (Math.abs((bx - ax) * (cy - ay) - (by - ay) * (cx - ax)) > 1e-9) kept.push(bx, by);
  }
  kept.push(outline[outline.length - 2]!, outline[outline.length - 1]!);
  return { x1, x2, outline: kept };
}

/** What else the level drawing needs to know this frame. */
export interface LevelDrawState {
  /** Pickups collected in the current attempt (hidden); null if unknown. */
  collected: ReadonlySet<number> | null;
  /** Pickups left of this x are hidden (racers watched from afar collect them unseen). */
  hidePickupsBefore: number;
  /** The racer's box while a tap would use an orb (that orb glows), else null. */
  orbFocus: { x: number; y: number; half: number } | null;
  /**
   * The moment of the level the view shows (the tick of its racer's attempt, fractional; see
   * LevelGuide.tickAt): where moving and turning things are drawn.
   */
  tick: number;
}

/** Spin of a saw blade's drawing (rad/s; it does nothing in the physics). */
const SAW_SPIN = 11;
/** The spin blur: a second, faint copy of the teeth this far back (rad). */
const SAW_BLUR = 0.16;

/**
 * The top of the still ground a moving block sinks into on its way, or null: of the ground under
 * the x range it sweeps, the highest face it can be both above and below (a lift rising out of a
 * spike pit, from under the pit's floor). A block never drawn into the ground stays whole.
 */
function sinkOf(objects: readonly LevelObject[], index: number): number | null {
  const o = objects[index]!;
  const swept = sweptBounds(o);
  let sink: number | null = null;
  for (const g of objects) {
    if (g.type !== 'block' || g.style !== 'ground' || g.move) continue;
    if (g.x >= swept.x2 - 1e-6 || g.x + g.w <= swept.x1 + 1e-6) continue;
    const face = g.y + g.h;
    if (face > swept.y1 + 1e-6 && face < swept.y2 - o.h + 1e-6 && (sink === null || face > sink)) {
      sink = face;
    }
  }
  return sink;
}

/** Rendering cache of one level in one theme. */
export class LevelArt {
  private readonly visible: number[] = [];
  private readonly emojiSprites = new Map<string, SizedSprite>();
  /** Ground and ceiling tones from the open face inwards (solid bands: cheapest fills). */
  private readonly earthBands: readonly [string, string, string];
  private readonly voidColor: string;
  private readonly voidEdge: string;
  /** A moving block's fill: lighter than a still block's, so it reads solid over a pit. */
  private readonly moverFill: string;
  /** Scratch: x-extents (screen) of the ground under the floor line, for finding pits. */
  private readonly covered = new Float64Array(64);
  /**
   * The ground pieces under the start and past the finish (indices into level.objects, -1 if
   * none). They run on for as far as a view can see: a camera that looks behind the start or
   * follows a finished racer a little further never shows the level's ground just ending.
   */
  private readonly runIn: number;
  private readonly runOut: number;
  /** Runs of slope columns, each one shape (see slopeShapes). */
  private readonly slopes: readonly SlopeShape[];
  /** Moving blocks and saws / rotors: their index in the World's lists, by object index. */
  private readonly moverOf = new Map<number, number>();
  private readonly roundOf = new Map<number, number>();
  /**
   * Moving blocks that sink into still ground on their way (a lift rising out of a spike pit
   * from below its floor): by object index, the top of that ground — they are drawn above it only.
   */
  private readonly sinks = new Map<number, number>();
  /**
   * Saws whose middle lies on a still block's face (half sunk into the ground, a ceiling, a
   * wall): they throw sparks there (see sparks). By object index: the face's height.
   */
  private readonly grinding = new Map<number, number>();
  /**
   * Still saws that cut into nothing, held on a pole from the nearest floor or ceiling above or
   * below them (a blade does not float): by object index, the face the pole goes to.
   */
  private readonly poles = new Map<number, number>();
  /** Scratch for moving things (no garbage per frame). */
  private readonly box: Aabb = { x1: 0, y1: 0, x2: 0, y2: 0 };
  private readonly offset: Offset = { x: 0, y: 0 };

  constructor(
    readonly level: LevelData,
    readonly world: World,
    readonly theme: Theme,
  ) {
    this.earthBands = [theme.ground, mix(theme.ground, theme.groundDark, 0.55), theme.groundDark];
    this.voidColor = mix(theme.groundDark, '#000000', 0.35);
    this.voidEdge = mix(theme.groundDark, theme.sky[2], 0.18);
    this.moverFill = mix(theme.block, theme.blockInner, 0.55);
    const objects = level.objects;
    let runIn = -1;
    let runOut = -1;
    for (let i = 0; i < objects.length; i++) {
      const o = objects[i]!;
      if (o.type !== 'block' || o.style !== 'ground') continue;
      if (runIn < 0 || o.x < objects[runIn]!.x) runIn = i;
      if (runOut < 0 || o.x + o.w > objects[runOut]!.x + objects[runOut]!.w) runOut = i;
    }
    // Only ground that reaches back to the start / on past the finish runs on.
    const first = objects[runIn];
    this.runIn = first && first.x <= level.start.x ? runIn : -1;
    const last = objects[runOut];
    this.runOut = last && last.x + last.w >= level.length ? runOut : -1;
    this.slopes = slopeShapes(objects);
    for (let i = 0; i < world.movers.length; i++) {
      const object = world.movers[i]!.object;
      this.moverOf.set(object, i);
      const sink = sinkOf(objects, object);
      if (sink !== null) this.sinks.set(object, sink);
    }
    world.rounds.forEach((r, i) => {
      this.roundOf.set(r.object, i);
      const o = objects[r.object];
      if (o?.type !== 'saw') return;
      const face = faceUnder(world, r.cx, r.cy);
      if (face !== null) this.grinding.set(r.object, face);
      else if (!o.move) {
        const holder = nearestFace(world, r.cx, r.cy);
        if (holder !== null) this.poles.set(r.object, holder);
      }
    });
  }

  draw(
    ctx: CanvasRenderingContext2D,
    view: ViewTransform,
    scale: number,
    time: number,
    state: LevelDrawState,
  ): void {
    const visible = this.visible;
    visible.length = 0;
    this.world.objectsInRange(view.left - 1, view.right + 1, visible);
    // The run-on ground is in view even where its piece has ended.
    const objects = this.level.objects;
    const first = objects[this.runIn];
    if (first && first.x > view.right + 1) visible.push(this.runIn);
    const last = objects[this.runOut];
    if (last && last.x + last.w < view.left - 1) visible.push(this.runOut);
    this.drawVoid(ctx, view);
    this.drawMoving(ctx, view, time, state.tick);
    this.drawBlocks(ctx, view);
    this.drawPortals(ctx, view, time);
    this.drawPads(ctx, view, time);
    this.drawOrbs(ctx, view, time, state);
    this.drawSpikes(ctx, view);
    this.drawMovers(ctx, view, state.tick);
    this.drawPickups(ctx, view, scale, time, state);
    this.drawFinish(ctx, view, time);
  }

  /** Where a block starts and ends; the run-on ground reaches out forever. */
  private startOf(i: number, o: LevelObject): number {
    return i === this.runIn ? Number.NEGATIVE_INFINITY : o.x;
  }

  private endOf(i: number, o: LevelObject): number {
    return i === this.runOut ? Number.POSITIVE_INFINITY : o.x + o.w;
  }

  /**
   * Darkness under the floor line where no ground is: pits look like holes. The backdrop
   * stops at the floor line, so this is also what fills the canvas there.
   */
  private drawVoid(ctx: CanvasRenderingContext2D, view: ViewTransform): void {
    const top = view.sy(0);
    const bottom = view.y + view.h;
    if (top >= bottom) return;
    // Screen x-extents of the ground covering everything from the floor line down.
    const covered = this.covered;
    let n = 0;
    const objects = this.level.objects;
    for (const i of this.visible) {
      const o = objects[i]!;
      if (o.type !== 'block' || o.style !== 'ground') continue;
      // Ground reaches down forever (see drawBlocks): it covers the floor line if its top does.
      if (o.y + o.h < -1e-6) continue;
      if (n + 2 > covered.length) break;
      // Insertion sort by start: there are only a few ground pieces in view.
      let k = n;
      const x1 = view.sx(this.startOf(i, o));
      while (k > 0 && covered[k - 2]! > x1) {
        covered[k] = covered[k - 2]!;
        covered[k + 1] = covered[k - 1]!;
        k -= 2;
      }
      covered[k] = x1;
      covered[k + 1] = view.sx(this.endOf(i, o));
      n += 2;
    }
    let x = view.x;
    const right = view.x + view.w;
    const edge = Math.min(bottom - top, Math.max(3, view.len(0.35)));
    for (let k = 0; k <= n; k += 2) {
      const gapEnd = k < n ? Math.min(right, covered[k]!) : right;
      if (gapEnd > x) {
        ctx.fillStyle = this.voidEdge;
        ctx.fillRect(x, top, gapEnd - x, edge);
        ctx.fillStyle = this.voidColor;
        ctx.fillRect(x, top + edge, gapEnd - x, bottom - top - edge);
      }
      if (k < n) x = Math.max(x, covered[k + 1]!);
      if (x >= right) break;
    }
  }

  private drawBlocks(ctx: CanvasRenderingContext2D, view: ViewTransform): void {
    const { theme } = this;
    const objects = this.level.objects;
    const ppb = view.ppb;
    const minX = view.x - 8;
    const maxX = view.x + view.w + 8;
    const viewBottom = view.y + view.h + 2;
    const viewTop = view.y - 2;

    // Fills: ground / ceiling bands, solid blocks. The ground is drawn as deep as the view
    // goes (tall views look further down than the level's ground blocks reach).
    ctx.fillStyle = theme.block;
    ctx.beginPath();
    for (const i of this.visible) {
      const o = objects[i]!;
      if (o.type !== 'block' || o.style === 'slope' || o.move) continue;
      const ground = o.style === 'ground';
      const x1 = Math.max(minX, view.sx(this.startOf(i, o)));
      const x2 = Math.min(maxX, view.sx(this.endOf(i, o)));
      const top = Math.max(viewTop, view.sy(o.y + o.h));
      const bottom = ground ? viewBottom : Math.min(viewBottom, view.sy(o.y));
      if (x2 <= x1 || bottom <= top) continue;
      if (ground || o.style === 'ceiling') {
        const faceTop = view.sy(o.y + o.h);
        const faceBottom = ground ? Math.max(viewBottom, view.sy(o.y)) : view.sy(o.y);
        this.drawEarth(ctx, view, ground ? 'ground' : 'ceiling', x1, x2, faceTop, faceBottom);
      } else {
        ctx.rect(x1, top, x2 - x1, bottom - top);
      }
    }
    ctx.fillStyle = theme.block;
    ctx.fill();
    // Slopes: one shape per run of columns, in a path of their own.
    ctx.beginPath();
    const slopes = this.slopePath(ctx, view, true);
    if (slopes) ctx.fill();

    // Tile patterns: a grid inside blocks, seams in the ground.
    ctx.beginPath();
    for (const i of this.visible) {
      const o = objects[i]!;
      if (o.type !== 'block' || o.move) continue;
      const style = o.style ?? 'block';
      if (style === 'platform' || style === 'slope') continue;
      const earth = style === 'ground' || style === 'ceiling';
      // Ground shows its seams near the surface only.
      const yFrom = earth && style === 'ground' ? Math.max(o.y, o.y + o.h - 2) : o.y;
      const yTo = earth && style === 'ceiling' ? Math.min(o.y + o.h, o.y + 2) : o.y + o.h;
      const from = this.startOf(i, o);
      const to = this.endOf(i, o);
      const xFrom = Math.max(Math.ceil(from + 0.001), Math.floor(view.left));
      const xTo = Math.min(to - 0.001, view.right + 1);
      for (let x = xFrom; x < xTo; x++) {
        const sx = view.sx(x);
        ctx.moveTo(sx, view.sy(yTo));
        ctx.lineTo(sx, view.sy(yFrom));
      }
      for (let y = Math.ceil(yFrom + 0.001); y < yTo - 0.001; y++) {
        const sy = view.sy(y);
        ctx.moveTo(Math.max(minX, view.sx(from)), sy);
        ctx.lineTo(Math.min(maxX, view.sx(to)), sy);
      }
    }
    ctx.strokeStyle = rgba(theme.blockInner, 0.9);
    ctx.lineWidth = Math.max(1, ppb * 0.04);
    ctx.stroke();

    // Edges: glow, then the bright line. Ground and ceilings only on their open face.
    for (let pass = 0; pass < 2; pass++) {
      ctx.beginPath();
      for (const i of this.visible) {
        const o = objects[i]!;
        // Slopes get their edge below, as one line per shape; moving blocks theirs on their own.
        if (o.type !== 'block' || o.style === 'slope' || o.move) continue;
        const x1 = Math.max(minX, view.sx(this.startOf(i, o)));
        const x2 = Math.min(maxX, view.sx(this.endOf(i, o)));
        if (x2 <= x1) continue;
        if (o.style === 'ground') {
          const y = view.sy(o.y + o.h);
          ctx.moveTo(x1, y);
          ctx.lineTo(x2, y);
        } else if (o.style === 'ceiling') {
          const y = view.sy(o.y);
          ctx.moveTo(x1, y);
          ctx.lineTo(x2, y);
        } else {
          const top = view.sy(o.y + o.h);
          const bottom = view.sy(o.y);
          if (bottom < viewTop || top > viewBottom) continue;
          ctx.rect(view.sx(o.x), top, view.len(o.w), bottom - top);
        }
      }
      // Slopes: their open side only (they stand on a floor or hang from a ceiling).
      if (slopes) this.slopePath(ctx, view, false);
      if (pass === 0) {
        ctx.strokeStyle = rgba(theme.line, 0.22);
        ctx.lineWidth = Math.max(4, ppb * 0.2);
      } else {
        ctx.strokeStyle = theme.blockLine;
        ctx.lineWidth = Math.max(1.5, ppb * 0.06);
      }
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
    // The ground line is brighter than block outlines: the floor must always be obvious.
    ctx.beginPath();
    for (const i of this.visible) {
      const o = objects[i]!;
      if (o.type !== 'block' || (o.style !== 'ground' && o.style !== 'ceiling')) continue;
      const x1 = Math.max(minX, view.sx(this.startOf(i, o)));
      const x2 = Math.min(maxX, view.sx(this.endOf(i, o)));
      if (x2 <= x1) continue;
      const y = view.sy(o.style === 'ground' ? o.y + o.h : o.y);
      ctx.moveTo(x1, y);
      ctx.lineTo(x2, y);
    }
    ctx.strokeStyle = theme.line;
    ctx.lineWidth = Math.max(2, ppb * 0.08);
    ctx.stroke();
  }

  /**
   * What moves or turns, at `tick` (see LevelDrawState.tick), under the level's still blocks:
   * tracks and rails first, then the rotors and the saw blades (moving blocks: drawMovers).
   */
  private drawMoving(
    ctx: CanvasRenderingContext2D,
    view: ViewTransform,
    time: number,
    tick: number,
  ): void {
    if (!this.world.dynamic && this.roundOf.size === 0) return;
    const objects = this.level.objects;
    const { theme } = this;
    // Tracks and rails: where each thing runs, a thin dark line with a dot at either end.
    ctx.beginPath();
    let tracks = false;
    for (const i of this.visible) {
      const o = objects[i]!;
      const pole = this.poles.get(i);
      if (pole !== undefined) {
        ctx.moveTo(view.sx(o.x + o.w / 2), view.sy(o.y + o.h / 2));
        ctx.lineTo(view.sx(o.x + o.w / 2), view.sy(pole));
        tracks = true;
        continue;
      }
      const move = (o.type === 'saw' || o.type === 'block') && o.move ? o.move : null;
      if (move === null) continue;
      trackPath(ctx, view, o.x + o.w / 2, o.y + o.h / 2, move);
      tracks = true;
    }
    if (tracks) {
      ctx.lineCap = 'round';
      ctx.strokeStyle = rgba(theme.blockInner, 0.95);
      ctx.lineWidth = Math.max(3, view.ppb * 0.16);
      ctx.stroke();
      ctx.strokeStyle = rgba(theme.blockLine, 0.35);
      ctx.lineWidth = Math.max(1, view.ppb * 0.05);
      ctx.stroke();
      ctx.lineCap = 'butt';
    }
    for (const i of this.visible) {
      const o = objects[i]!;
      if (o.type === 'rotor') this.drawRotor(ctx, view, o, tick);
    }
    for (const i of this.visible) {
      const o = objects[i]!;
      if (o.type === 'saw') this.drawSaw(ctx, view, o, time, tick);
    }
  }

  /**
   * The moving blocks where they are at `tick`, over the level's spikes (see the file's comment):
   * each a solid slab with its own tile pattern.
   */
  private drawMovers(ctx: CanvasRenderingContext2D, view: ViewTransform, tick: number): void {
    if (this.moverOf.size === 0) return;
    const objects = this.level.objects;
    for (const i of this.visible) {
      const o = objects[i]!;
      if (o.type === 'block' && o.move) this.drawMover(ctx, view, i, tick);
    }
  }

  /**
   * A moving block where it is at `tick`: a block (or a platform) with its own tile pattern and a
   * bright top face, filled lighter than a still block (it passes over the dark of pits); above
   * the still ground it sinks into only (see sinks).
   */
  private drawMover(ctx: CanvasRenderingContext2D, view: ViewTransform, i: number, tick: number) {
    const index = this.moverOf.get(i);
    if (index === undefined) return;
    const { theme } = this;
    const b = this.world.moverBox(index, tick, this.box);
    const sink = this.sinks.get(i) ?? Number.NEGATIVE_INFINITY;
    if (b.y2 <= sink + 1e-6) return;
    const y1 = Math.max(b.y1, sink);
    const x1 = view.sx(b.x1);
    const x2 = view.sx(b.x2);
    const top = view.sy(b.y2);
    const bottom = view.sy(y1);
    if (x2 < view.x - 8 || x1 > view.x + view.w + 8) return;
    ctx.fillStyle = this.moverFill;
    ctx.fillRect(x1, top, x2 - x1, bottom - top);
    // Its seams move with it.
    const o = this.level.objects[i]!;
    if (o.type === 'block' && o.style !== 'platform') {
      ctx.beginPath();
      for (let x = 1; x < b.x2 - b.x1 - 1e-6; x++) {
        ctx.moveTo(view.sx(b.x1 + x), top);
        ctx.lineTo(view.sx(b.x1 + x), bottom);
      }
      for (let y = 1; b.y1 + y < b.y2 - 1e-6; y++) {
        if (b.y1 + y <= y1) continue;
        ctx.moveTo(x1, view.sy(b.y1 + y));
        ctx.lineTo(x2, view.sy(b.y1 + y));
      }
      ctx.strokeStyle = rgba(theme.blockInner, 0.9);
      ctx.lineWidth = Math.max(1, view.ppb * 0.04);
      ctx.stroke();
    }
    // The outline: glow, then the bright line (no bottom edge where it sinks into the ground).
    ctx.beginPath();
    if (y1 > b.y1) {
      ctx.moveTo(x1, bottom);
      ctx.lineTo(x1, top);
      ctx.lineTo(x2, top);
      ctx.lineTo(x2, bottom);
    } else {
      ctx.rect(x1, top, x2 - x1, bottom - top);
    }
    ctx.lineJoin = 'round';
    ctx.strokeStyle = rgba(theme.line, 0.22);
    ctx.lineWidth = Math.max(4, view.ppb * 0.2);
    ctx.stroke();
    ctx.strokeStyle = theme.blockLine;
    ctx.lineWidth = Math.max(1.5, view.ppb * 0.06);
    ctx.stroke();
    // The top face it carries the player on, brighter still.
    ctx.beginPath();
    ctx.moveTo(x1, top);
    ctx.lineTo(x2, top);
    ctx.strokeStyle = theme.line;
    ctx.lineWidth = Math.max(2, view.ppb * 0.08);
    ctx.stroke();
  }

  /**
   * A rotor at `tick`: a bar of spikes along each arm, or a spiked ball on a chain, round a
   * bolted hub (drawn bigger than they kill, like spikes).
   */
  private drawRotor(
    ctx: CanvasRenderingContext2D,
    view: ViewTransform,
    o: LevelObject & { type: 'rotor' },
    tick: number,
  ): void {
    const { theme } = this;
    const cx = o.x + o.w / 2;
    const cy = o.y + o.h / 2;
    const reach = o.w / 2;
    const arms = Math.max(1, Math.min(4, Math.round(o.arms)));
    const px = view.sx(cx);
    const py = view.sy(cy);
    ctx.lineJoin = 'round';
    for (let k = 0; k < arms; k++) {
      const turn = rotorTurn(o.period, o.phase, arms, k, tick);
      // Screen y grows down: the arm's direction on screen.
      const dx = turnCos(turn);
      const dy = -turnSin(turn);
      if (o.arm === 'mace') {
        const d = Math.max(0, reach - ROTOR.maceInset);
        // The chain: links from the hub to the ball.
        ctx.fillStyle = rgba(theme.spikeLine, 0.75);
        const links = Math.max(1, Math.floor(d / 0.32));
        for (let l = 1; l < links; l++) {
          const t = (l / links) * d;
          const s = view.len(0.07);
          ctx.fillRect(px + dx * view.len(t) - s, py + dy * view.len(t) - s, 2 * s, 2 * s);
        }
        spikedBall(ctx, px + dx * view.len(d), py + dy * view.len(d), view.len(0.55), turn, theme);
      } else {
        spikedBar(ctx, view, px, py, dx, dy, reach, theme);
      }
    }
    // The hub and its bolt.
    ctx.beginPath();
    ctx.arc(px, py, view.len(0.3), 0, Math.PI * 2);
    ctx.fillStyle = theme.spike;
    ctx.fill();
    ctx.strokeStyle = theme.spikeLine;
    ctx.lineWidth = Math.max(1.5, view.ppb * 0.06);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(px, py, view.len(0.1), 0, Math.PI * 2);
    ctx.fillStyle = theme.spikeLine;
    ctx.fill();
  }

  /**
   * A saw blade where its motion has it at `tick`, spinning (in real time: its spin does
   * nothing in the physics), with a faint second set of teeth behind as its spin blur.
   */
  private drawSaw(
    ctx: CanvasRenderingContext2D,
    view: ViewTransform,
    o: LevelObject & { type: 'saw' },
    time: number,
    tick: number,
  ): void {
    const { theme } = this;
    let cx = o.x + o.w / 2;
    let cy = o.y + o.h / 2;
    if (o.move) {
      const d = motionOffset(o.move, tick, this.offset);
      cx += d.x;
      cy += d.y;
    }
    const r = view.len(o.w / 2);
    const px = view.sx(cx);
    const py = view.sy(cy);
    if (px + r < view.x || px - r > view.x + view.w) return;
    const teeth = Math.max(8, Math.min(18, Math.round(6 + o.w * 4)));
    // Smaller blades spin faster (as if they rolled at one speed).
    const angle = (time * SAW_SPIN) / Math.max(0.6, o.w / 2);
    ctx.lineJoin = 'round';
    ctx.globalAlpha = 0.3;
    sawPath(ctx, px, py, r, teeth, angle - SAW_BLUR);
    ctx.fillStyle = theme.spikeLine;
    ctx.fill();
    ctx.globalAlpha = 1;
    sawPath(ctx, px, py, r, teeth, angle);
    ctx.fillStyle = theme.spike;
    ctx.fill();
    ctx.strokeStyle = theme.spikeLine;
    ctx.lineWidth = Math.max(1.5, view.ppb * 0.055);
    ctx.stroke();
    // The rim around the hub, and the hub.
    ctx.beginPath();
    ctx.arc(px, py, r * 0.55, 0, Math.PI * 2);
    ctx.strokeStyle = rgba(theme.spikeLine, 0.45);
    ctx.lineWidth = Math.max(1, view.ppb * 0.05);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(px, py, r * 0.16, 0, Math.PI * 2);
    ctx.fillStyle = theme.spikeLine;
    ctx.fill();
  }

  /** Whether any saw blade of the level cuts into a surface (see sparks). */
  get hasSparks(): boolean {
    return this.grinding.size > 0;
  }

  /**
   * Where the saw blades in view cut into a surface at `tick` (half sunk into the ground, a
   * ceiling or a wall): (x, y, side) triples into `out` — side +1 where the blade sticks out
   * upward, −1 downward; returns how many.
   */
  sparks(view: ViewTransform, tick: number, out: number[]): number {
    out.length = 0;
    const objects = this.level.objects;
    for (const i of this.visible) {
      const face = this.grinding.get(i);
      if (face === undefined) continue;
      const o = objects[i]!;
      if (o.type !== 'saw') continue;
      let cx = o.x + o.w / 2;
      if (o.move) cx += motionOffset(o.move, tick, this.offset).x;
      if (cx < view.left - 1 || cx > view.right + 1) continue;
      out.push(cx, face, face <= o.y + o.h / 2 + 1e-6 ? 1 : -1);
    }
    return out.length / 3;
  }

  /**
   * Adds the slopes in view to the current path: whole shapes (`closed`, along the surface
   * they stand on) or only their open sides. Returns whether there was any.
   */
  private slopePath(ctx: CanvasRenderingContext2D, view: ViewTransform, closed: boolean): boolean {
    let any = false;
    for (const shape of this.slopes) {
      if (shape.x2 < view.left - 1 || shape.x1 > view.right + 1) continue;
      const points = shape.outline;
      ctx.moveTo(view.sx(points[0]!), view.sy(points[1]!));
      for (let k = 2; k < points.length; k += 2) {
        ctx.lineTo(view.sx(points[k]!), view.sy(points[k + 1]!));
      }
      if (closed) ctx.closePath();
      any = true;
    }
    return any;
  }

  /** Ground or ceiling fill: solid bands, lightest at the open face. */
  private drawEarth(
    ctx: CanvasRenderingContext2D,
    view: ViewTransform,
    style: 'ground' | 'ceiling',
    x1: number,
    x2: number,
    top: number,
    bottom: number,
  ): void {
    const from = Math.max(top, view.y - 2);
    const to = Math.min(bottom, view.y + view.h + 2);
    if (to <= from) return;
    const first = view.len(0.35);
    const second = view.len(2);
    const bands = this.earthBands;
    for (let band = 0; band < 3; band++) {
      // Distances from the open face (top of the ground, bottom of a ceiling).
      const near = band === 0 ? 0 : band === 1 ? first : first + second;
      const far = band === 0 ? first : band === 1 ? first + second : Number.POSITIVE_INFINITY;
      const y1 = style === 'ground' ? Math.max(from, top + near) : Math.max(from, bottom - far);
      const y2 = style === 'ground' ? Math.min(to, top + far) : Math.min(to, bottom - near);
      if (y2 <= y1) continue;
      ctx.fillStyle = bands[band]!;
      ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
    }
  }

  private drawSpikes(ctx: CanvasRenderingContext2D, view: ViewTransform): void {
    const { theme } = this;
    const objects = this.level.objects;
    ctx.beginPath();
    let any = false;
    for (const i of this.visible) {
      const o = objects[i]!;
      if (o.type !== 'spike') continue;
      spikePath(ctx, view, o);
      any = true;
    }
    if (!any) return;
    ctx.fillStyle = theme.spike;
    ctx.fill();
    ctx.lineJoin = 'round';
    ctx.strokeStyle = rgba(theme.spikeLine, 0.25);
    ctx.lineWidth = Math.max(4, view.ppb * 0.16);
    ctx.stroke();
    ctx.strokeStyle = theme.spikeLine;
    ctx.lineWidth = Math.max(1.5, view.ppb * 0.055);
    ctx.stroke();
    // A darker inner line gives the spikes some depth.
    ctx.beginPath();
    for (const i of this.visible) {
      const o = objects[i]!;
      if (o.type !== 'spike') continue;
      spikePath(ctx, view, o, 0.32);
    }
    ctx.fillStyle = rgba(theme.spikeLine, 0.14);
    ctx.fill();
  }

  private drawPads(ctx: CanvasRenderingContext2D, view: ViewTransform, time: number): void {
    const objects = this.level.objects;
    for (const i of this.visible) {
      const o = objects[i]!;
      if (o.type !== 'pad') continue;
      const color = PAD_COLORS[o.color] ?? PAD_COLORS.yellow;
      const up = o.dir !== 'down';
      const cx = view.sx(o.x + o.w / 2);
      const base = view.sy(up ? o.y : o.y + o.h);
      const rx = view.len(o.w * 0.46);
      const ry = view.len(Math.max(0.22, o.h));
      const pulse = 1 + Math.sin(time * 7 + i) * 0.08;
      const glow = glowSprite(color);
      if (glow) {
        const size = view.len(1.4) * pulse;
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 0.75;
        ctx.drawImage(glow, cx - size / 2, base - (up ? size * 0.62 : size * 0.38), size, size);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
      }
      ctx.beginPath();
      ctx.ellipse(cx, base, rx, ry, 0, up ? Math.PI : 0, up ? Math.PI * 2 : Math.PI);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = '#0a0614';
      ctx.lineWidth = Math.max(1, view.ppb * 0.04);
      ctx.stroke();
      ctx.beginPath();
      ctx.ellipse(cx, base, rx * 0.55, ry * 0.5, 0, up ? Math.PI : 0, up ? Math.PI * 2 : Math.PI);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
      ctx.fill();
    }
  }

  private drawOrbs(
    ctx: CanvasRenderingContext2D,
    view: ViewTransform,
    time: number,
    state: LevelDrawState,
  ): void {
    const objects = this.level.objects;
    const focus = state.orbFocus;
    for (const i of this.visible) {
      const o = objects[i]!;
      if (o.type !== 'orb') continue;
      const color = ORB_COLORS[o.color] ?? ORB_COLORS.yellow;
      const cx = view.sx(o.x + o.w / 2);
      const cy = view.sy(o.y + o.h / 2);
      const r = view.len(Math.min(o.w, o.h) * 0.36);
      const hot =
        focus !== null &&
        Math.abs(focus.x - (o.x + o.w / 2)) < o.w / 2 + 0.2 + focus.half &&
        Math.abs(focus.y - (o.y + o.h / 2)) < o.h / 2 + 0.2 + focus.half;
      const pulse = 1 + Math.sin(time * 6 + i) * (hot ? 0.14 : 0.06);
      const glow = glowSprite(color);
      if (glow) {
        const size = r * (hot ? 5.2 : 3.6) * pulse;
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = hot ? 1 : 0.7;
        ctx.drawImage(glow, cx - size / 2, cy - size / 2, size, size);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
      }
      // Body with a highlight.
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.lineWidth = Math.max(1, view.ppb * 0.045);
      ctx.strokeStyle = '#0a0614';
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx - r * 0.3, cy - r * 0.3, r * 0.32, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.fill();
      // Spinning ring.
      const ring = r * 1.42 * pulse;
      const spin = time * 2.6 + i;
      ctx.lineCap = 'round';
      ctx.strokeStyle = hot ? '#ffffff' : color;
      ctx.lineWidth = Math.max(1.5, view.ppb * (hot ? 0.09 : 0.065));
      ctx.beginPath();
      ctx.arc(cx, cy, ring, spin, spin + Math.PI * 0.75);
      ctx.moveTo(cx + Math.cos(spin + Math.PI) * ring, cy + Math.sin(spin + Math.PI) * ring);
      ctx.arc(cx, cy, ring, spin + Math.PI, spin + Math.PI * 1.75);
      ctx.stroke();
      ctx.lineCap = 'butt';
    }
  }

  private drawPortals(ctx: CanvasRenderingContext2D, view: ViewTransform, time: number): void {
    const objects = this.level.objects;
    for (const i of this.visible) {
      const o = objects[i]!;
      if (o.type !== 'portal') continue;
      const color = PORTAL_COLORS[o.portal] ?? '#ffffff';
      const cx = view.sx(o.x + o.w / 2);
      const cy = view.sy(o.y + o.h / 2);
      const w = view.len(Math.max(0.8, o.w * 0.9));
      const h = view.len(o.h);
      const glow = glowSprite(color);
      if (glow) {
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 0.55;
        ctx.drawImage(glow, cx - w * 1.3, cy - h * 0.62, w * 2.6, h * 1.24);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
      }
      capsulePath(ctx, cx, cy, w, h);
      ctx.fillStyle = rgba(color, 0.2);
      ctx.fill();
      ctx.strokeStyle = rgba(color, 0.35);
      ctx.lineWidth = Math.max(4, view.ppb * 0.3);
      ctx.stroke();
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(2, view.ppb * 0.12);
      ctx.stroke();
      capsulePath(ctx, cx, cy, w * 0.62, h - w * 0.38);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
      ctx.lineWidth = Math.max(1, view.ppb * 0.035);
      ctx.stroke();
      // Sparks drifting inside the portal.
      ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
      for (let k = 0; k < 3; k++) {
        const phase = (time * 0.6 + k / 3 + i * 0.37) % 1;
        const sy = cy + (phase - 0.5) * (h - w) * 0.9;
        const sx = cx + Math.sin(time * 3 + k * 2.1) * w * 0.18;
        ctx.fillRect(sx - 1.5, sy - 1.5, 3, 3);
      }
      drawPortalIcon(ctx, o.portal, cx, cy, view.len(0.52));
    }
  }

  private drawPickups(
    ctx: CanvasRenderingContext2D,
    view: ViewTransform,
    scale: number,
    time: number,
    state: LevelDrawState,
  ): void {
    const objects = this.level.objects;
    for (const i of this.visible) {
      const o = objects[i]!;
      if (o.type !== 'pickup') continue;
      if (state.collected?.has(i) || o.x + o.w < state.hidePickupsBefore) continue;
      const boost = BOOSTS[o.boost];
      const color = boost?.color ?? '#ffffff';
      const bob = Math.sin(time * 3 + o.x) * 0.1;
      const cx = view.sx(o.x + o.w / 2);
      const cy = view.sy(o.y + o.h / 2 + bob);
      const size = view.len(Math.min(o.w, o.h) * 0.8);
      const glow = glowSprite(color);
      if (glow) {
        const g = size * (2.1 + Math.sin(time * 5 + i) * 0.15);
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 0.8;
        ctx.drawImage(glow, cx - g / 2, cy - g / 2, g, g);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
      }
      const wobble = Math.sin(time * 2.2 + i) * 0.12;
      roundedSquarePath(ctx, cx, cy, size, size * 0.3, wobble);
      ctx.fillStyle = 'rgba(10, 6, 20, 0.82)';
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(2, view.ppb * 0.08);
      ctx.stroke();
      const emoji = this.emoji(boost?.emoji ?? '?', size * 0.72 * scale);
      if (emoji) stamp(ctx, scale, emoji, cx, cy, size * 0.72, size * 0.72, wobble);
    }
  }

  /** The boost emoji at the current zoom (redrawn only when the size step changes). */
  private emoji(emoji: string, devicePx: number): HTMLCanvasElement | null {
    let sprite = this.emojiSprites.get(emoji);
    if (!sprite) {
      sprite = new SizedSprite((size) => drawEmojiSprite(emoji, size));
      this.emojiSprites.set(emoji, sprite);
    }
    return sprite.get(devicePx);
  }

  private drawFinish(ctx: CanvasRenderingContext2D, view: ViewTransform, time: number): void {
    const x = this.level.length;
    if (x < view.left - 1 || x > view.right + 1) return;
    const cell = 0.5;
    const top = Math.min(view.top + 1, 40);
    const sx = view.sx(x);
    const glow = glowSprite(this.theme.accent);
    if (glow) {
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 0.45 + Math.sin(time * 4) * 0.1;
      ctx.drawImage(glow, sx - view.len(1.5), view.y, view.len(3), view.h);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }
    const size = view.len(cell);
    ctx.globalAlpha = 0.9;
    for (let y = Math.floor(Math.max(view.bottom, -6) / cell) * cell; y < top; y += cell) {
      const row = Math.round(y / cell);
      const syTop = view.sy(y + cell);
      for (let col = 0; col < 2; col++) {
        ctx.fillStyle = (row + col) % 2 === 0 ? '#ffffff' : '#0a0614';
        ctx.fillRect(sx + (col - 1) * size, syTop, size, size);
      }
    }
    ctx.globalAlpha = 1;
  }
}

/**
 * The face of a still block a saw's middle lies on (half sunk into the ground, a ceiling, a
 * wall's top or bottom), or null.
 */
function faceUnder(world: World, cx: number, cy: number): number | null {
  const solids = world.solids;
  for (let id = 0; id < solids.count; id++) {
    if (solids.x1[id]! > cx || solids.x2[id]! < cx) continue;
    if (Math.abs(solids.y2[id]! - cy) < 0.05) return solids.y2[id]!;
    if (Math.abs(solids.y1[id]! - cy) < 0.05) return solids.y1[id]!;
  }
  return null;
}

/**
 * The face of the nearest still block straight above or below (cx, cy), at most POLE_REACH away
 * (a floor's top or a ceiling's underside), or null.
 */
function nearestFace(world: World, cx: number, cy: number): number | null {
  const solids = world.solids;
  let best: number | null = null;
  for (let id = 0; id < solids.count; id++) {
    if (solids.x1[id]! > cx || solids.x2[id]! < cx) continue;
    const face =
      solids.y2[id]! <= cy ? solids.y2[id]! : solids.y1[id]! >= cy ? solids.y1[id]! : null;
    if (face === null || Math.abs(face - cy) > POLE_REACH) continue;
    if (best === null || Math.abs(face - cy) < Math.abs(best - cy)) best = face;
  }
  return best;
}

/** How far a still saw's pole reaches for a floor or a ceiling (blocks). */
const POLE_REACH = 4;

/** Adds the track of a motion around (cx, cy) to the current path: a line, or a circle. */
function trackPath(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  cx: number,
  cy: number,
  move: Motion,
): void {
  if (move.path === 'circle') {
    const rx = view.len(Math.abs(move.dx));
    const ry = view.len(Math.abs(move.dy));
    ctx.moveTo(view.sx(cx) + rx, view.sy(cy));
    ctx.ellipse(view.sx(cx), view.sy(cy), rx, ry, 0, 0, Math.PI * 2);
    return;
  }
  const ax = view.sx(cx - move.dx);
  const ay = view.sy(cy - move.dy);
  const bx = view.sx(cx + move.dx);
  const by = view.sy(cy + move.dy);
  ctx.moveTo(ax, ay);
  ctx.lineTo(bx, by);
  // End stops.
  const s = view.len(0.12);
  ctx.moveTo(ax + s, ay);
  ctx.arc(ax, ay, s, 0, Math.PI * 2);
  ctx.moveTo(bx + s, by);
  ctx.arc(bx, by, s, 0, Math.PI * 2);
}

/** A saw blade's outline (`teeth` teeth, slanted like a circular saw's) as the current path. */
function sawPath(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  teeth: number,
  angle: number,
): void {
  const inner = r * 0.78;
  const step = (Math.PI * 2) / teeth;
  ctx.beginPath();
  for (let k = 0; k < teeth; k++) {
    const a = angle + k * step;
    // A tooth: up its long back to the tip, straight down its front.
    const b = a + step * 0.72;
    if (k === 0) ctx.moveTo(cx + Math.cos(a) * inner, cy + Math.sin(a) * inner);
    else ctx.lineTo(cx + Math.cos(a) * inner, cy + Math.sin(a) * inner);
    ctx.lineTo(cx + Math.cos(b) * r, cy + Math.sin(b) * r);
    ctx.lineTo(cx + Math.cos(b) * inner, cy + Math.sin(b) * inner);
  }
  ctx.closePath();
}

/**
 * A rotor's spiked bar from the hub at (px, py) along the screen direction (dx, dy), `reach`
 * blocks: a dark bar with spikes along both sides and at its tip.
 */
function spikedBar(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  px: number,
  py: number,
  dx: number,
  dy: number,
  reach: number,
  theme: Theme,
): void {
  // The bar's body about as wide as it kills (ROTOR.barRadius), its spikes forgiven.
  const len = view.len(reach - 0.15);
  const half = view.len(0.22);
  const spike = view.len(0.28);
  // Across the bar.
  const nx = -dy;
  const ny = dx;
  ctx.beginPath();
  ctx.moveTo(px + nx * half, py + ny * half);
  // One side's spikes, the tip's, then the other side's back to the hub.
  const count = Math.max(1, Math.floor((reach - 0.35) / 0.45));
  for (let side = 1; side >= -1; side -= 2) {
    for (let k = 0; k < count; k++) {
      const t = side > 0 ? k : count - 1 - k;
      const at = view.len(0.45 + t * 0.45);
      const bx = px + dx * at;
      const by = py + dy * at;
      const w = view.len(0.14);
      ctx.lineTo(bx - dx * w * side + nx * half * side, by - dy * w * side + ny * half * side);
      ctx.lineTo(bx + nx * (half + spike) * side, by + ny * (half + spike) * side);
      ctx.lineTo(bx + dx * w * side + nx * half * side, by + dy * w * side + ny * half * side);
    }
    if (side > 0) {
      // The tip.
      ctx.lineTo(px + dx * len + nx * half, py + dy * len + ny * half);
      ctx.lineTo(px + dx * (len + spike), py + dy * (len + spike));
      ctx.lineTo(px + dx * len - nx * half, py + dy * len - ny * half);
    }
  }
  ctx.lineTo(px - nx * half, py - ny * half);
  ctx.closePath();
  ctx.fillStyle = theme.spike;
  ctx.fill();
  ctx.strokeStyle = theme.spikeLine;
  ctx.lineWidth = Math.max(1.5, view.ppb * 0.055);
  ctx.stroke();
}

/** A mace's spiked ball at (px, py), `r` px across its body, turned with the rotor. */
function spikedBall(
  ctx: CanvasRenderingContext2D,
  px: number,
  py: number,
  r: number,
  turn: number,
  theme: Theme,
): void {
  const spikes = 8;
  const step = (Math.PI * 2) / spikes;
  const spin = -turn * Math.PI * 2;
  ctx.beginPath();
  for (let k = 0; k < spikes; k++) {
    const a = spin + k * step;
    const point = (angle: number, radius: number) =>
      [px + Math.cos(angle) * radius, py + Math.sin(angle) * radius] as const;
    const [ax, ay] = point(a - step * 0.28, r * 0.92);
    const [tx, ty] = point(a, r * 1.45);
    const [bx, by] = point(a + step * 0.28, r * 0.92);
    if (k === 0) ctx.moveTo(ax, ay);
    else ctx.lineTo(ax, ay);
    ctx.lineTo(tx, ty);
    ctx.lineTo(bx, by);
  }
  ctx.closePath();
  ctx.fillStyle = theme.spike;
  ctx.fill();
  ctx.strokeStyle = theme.spikeLine;
  ctx.lineWidth = Math.max(1.5, r * 0.1);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(px - r * 0.3, py - r * 0.3, r * 0.22, 0, Math.PI * 2);
  ctx.fillStyle = rgba(theme.spikeLine, 0.5);
  ctx.fill();
}

/** Adds a spike's triangle (optionally shrunk to `inset` of its size) to the current path. */
function spikePath(
  ctx: CanvasRenderingContext2D,
  view: ViewTransform,
  o: SpikeObject,
  inset = 0,
): void {
  const { x, y, w, h } = o;
  const k = inset;
  let ax: number;
  let ay: number;
  let bx: number;
  let by: number;
  let tx: number;
  let ty: number;
  switch (o.dir) {
    case 'up':
      ax = x + w * k * 0.5;
      ay = y + h * k * 0.15;
      bx = x + w * (1 - k * 0.5);
      by = ay;
      tx = x + w / 2;
      ty = y + h * (1 - k * 0.6);
      break;
    case 'down':
      ax = x + w * k * 0.5;
      ay = y + h * (1 - k * 0.15);
      bx = x + w * (1 - k * 0.5);
      by = ay;
      tx = x + w / 2;
      ty = y + h * k * 0.6;
      break;
    case 'right':
      ax = x + w * k * 0.15;
      ay = y + h * k * 0.5;
      bx = ax;
      by = y + h * (1 - k * 0.5);
      tx = x + w * (1 - k * 0.6);
      ty = y + h / 2;
      break;
    default:
      ax = x + w * (1 - k * 0.15);
      ay = y + h * k * 0.5;
      bx = ax;
      by = y + h * (1 - k * 0.5);
      tx = x + w * k * 0.6;
      ty = y + h / 2;
      break;
  }
  ctx.moveTo(view.sx(ax), view.sy(ay));
  ctx.lineTo(view.sx(tx), view.sy(ty));
  ctx.lineTo(view.sx(bx), view.sy(by));
  ctx.closePath();
}

/** A vertical capsule (stadium) centred at (cx, cy), w wide and h tall. */
function capsulePath(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  w: number,
  h: number,
): void {
  const r = Math.min(w / 2, h / 2);
  const half = Math.max(0, h / 2 - r);
  ctx.beginPath();
  ctx.arc(cx, cy - half, r, Math.PI, 0);
  ctx.lineTo(cx + r, cy + half);
  ctx.arc(cx, cy + half, r, 0, Math.PI);
  ctx.closePath();
}

/** A rounded square centred at (cx, cy), rotated by `angle` (no closures: drawn every frame). */
function roundedSquarePath(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  radius: number,
  angle: number,
): void {
  const h = size / 2;
  // Half-diagonals of the rotated square: corner = centre ± a ± b.
  const ax = h * Math.cos(angle);
  const ay = h * Math.sin(angle);
  const bx = -ay;
  const by = ax;
  // Corners: top-left, top-right, bottom-right, bottom-left (before rotation).
  const x1 = cx - ax - bx;
  const y1 = cy - ay - by;
  const x2 = cx + ax - bx;
  const y2 = cy + ay - by;
  const x3 = cx + ax + bx;
  const y3 = cy + ay + by;
  const x4 = cx - ax + bx;
  const y4 = cy - ay + by;
  const k = radius / size;
  ctx.beginPath();
  ctx.moveTo(x1 + (x2 - x1) * k, y1 + (y2 - y1) * k);
  ctx.arcTo(x2, y2, x3, y3, radius);
  ctx.arcTo(x3, y3, x4, y4, radius);
  ctx.arcTo(x4, y4, x1, y1, radius);
  ctx.arcTo(x1, y1, x2, y2, radius);
  ctx.closePath();
}

/** White pictogram in a portal: what it does (gravity arrow, mode silhouette, speed chevrons). */
function drawPortalIcon(
  ctx: CanvasRenderingContext2D,
  portal: PortalType,
  cx: number,
  cy: number,
  size: number,
): void {
  const s = size / 2;
  ctx.beginPath();
  switch (portal) {
    case 'gravity-normal':
    case 'gravity-reverse': {
      const d = portal === 'gravity-normal' ? 1 : -1;
      ctx.moveTo(cx, cy + s * d);
      ctx.lineTo(cx + s * 0.8, cy);
      ctx.lineTo(cx + s * 0.3, cy);
      ctx.lineTo(cx + s * 0.3, cy - s * d);
      ctx.lineTo(cx - s * 0.3, cy - s * d);
      ctx.lineTo(cx - s * 0.3, cy);
      ctx.lineTo(cx - s * 0.8, cy);
      ctx.closePath();
      break;
    }
    case 'mode-cube':
      ctx.rect(cx - s * 0.7, cy - s * 0.7, s * 1.4, s * 1.4);
      break;
    case 'mode-ship':
      ctx.moveTo(cx - s, cy + s * 0.2);
      ctx.lineTo(cx - s * 0.6, cy - s * 0.45);
      ctx.lineTo(cx + s * 0.35, cy - s * 0.45);
      ctx.lineTo(cx + s, cy + s * 0.1);
      ctx.lineTo(cx + s * 0.4, cy + s * 0.55);
      ctx.lineTo(cx - s * 0.7, cy + s * 0.55);
      ctx.closePath();
      break;
    case 'mode-ball':
      ctx.arc(cx, cy, s * 0.8, 0, Math.PI * 2);
      break;
    case 'mode-ufo':
      ctx.ellipse(cx, cy + s * 0.2, s, s * 0.38, 0, 0, Math.PI * 2);
      ctx.moveTo(cx + s * 0.5, cy + s * 0.05);
      ctx.arc(cx, cy + s * 0.05, s * 0.5, 0, Math.PI, true);
      break;
    case 'mode-wave':
      ctx.moveTo(cx + s, cy);
      ctx.lineTo(cx - s * 0.7, cy - s * 0.75);
      ctx.lineTo(cx - s * 0.3, cy);
      ctx.lineTo(cx - s * 0.7, cy + s * 0.75);
      ctx.closePath();
      break;
    case 'mode-robot':
      // A box on two legs.
      ctx.rect(cx - s * 0.6, cy - s * 0.85, s * 1.2, s * 0.95);
      ctx.rect(cx - s * 0.45, cy + s * 0.1, s * 0.3, s * 0.75);
      ctx.rect(cx + s * 0.15, cy + s * 0.1, s * 0.3, s * 0.75);
      break;
    case 'mode-spider':
      // A box on bent legs, two a side (stroked first, under it).
      spiderLegs(ctx, cx, cy, s);
      ctx.beginPath();
      ctx.rect(cx - s * 0.45, cy - s * 0.5, s * 0.9, s * 0.75);
      break;
    default: {
      // Speed portals: one chevron per speed step.
      const count = SPEED_CHEVRONS[portal] ?? 1;
      const step = s * 0.55;
      const start = cx - ((count - 1) * step) / 2;
      for (let k = 0; k < count; k++) {
        const x = start + k * step;
        ctx.moveTo(x - s * 0.3, cy - s * 0.6);
        ctx.lineTo(x + s * 0.25, cy);
        ctx.lineTo(x - s * 0.3, cy + s * 0.6);
        ctx.lineTo(x - s * 0.05, cy);
        ctx.closePath();
      }
      break;
    }
  }
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = '#0a0614';
  ctx.lineWidth = Math.max(1, s * 0.16);
  ctx.lineJoin = 'round';
  ctx.stroke();
}

/** Left and right legs of the spider pictogram. */
const LEG_SIDES = [-1, 1] as const;

/** The spider pictogram's legs: two a side, knees up, white with a dark outline. */
function spiderLegs(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number): void {
  ctx.beginPath();
  for (const side of LEG_SIDES) {
    for (let k = 0; k < 2; k++) {
      const hip = cy - s * (0.3 - 0.35 * k);
      ctx.moveTo(cx + side * s * 0.3, hip);
      ctx.lineTo(cx + side * s * (0.72 + 0.12 * k), hip - s * 0.4);
      ctx.lineTo(cx + side * s * (0.92 + 0.08 * k), cy + s * 0.85);
    }
  }
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = '#0a0614';
  ctx.lineWidth = Math.max(2.5, s * 0.36);
  ctx.stroke();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = Math.max(1.2, s * 0.18);
  ctx.stroke();
  ctx.lineCap = 'butt';
}
